/**
 * Redux slice for DefectsGTC module
 * Uses clientQuery/clientMutation for GraphQL API access
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { resetALL } from 'Redux/actions';
import { v4 as uuidv4 } from 'uuid';
import { clientQuery, clientMutation } from '../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../core/defaultValues';

// ============================================================================
// Constants
// ============================================================================

const DEFECT_STATUS_ALL_OPEN = -8;
const DEFECT_STATUS_NEW_ONLY = -10;
const DEFECT_STATUS_PENDING_ONLY = -6;

// Map numeric status to GraphQL enum values
const mapDefectStatusToEnum = (status) => {
	const numStatus = Number(status);
	switch (numStatus) {
		case DEFECT_STATUS_NEW_ONLY:
			return ['OPEN'];
		case DEFECT_STATUS_PENDING_ONLY:
			return ['PENDING'];
		case DEFECT_STATUS_ALL_OPEN:
		default:
			return ['OPEN', 'PENDING'];
	}
};

// Map sort fields from component to GraphQL
const mapSortField = (field) => {
	const mapping = {
		fleet: 'assetLabel',
		assetLabel: 'assetLabel',
		vin: 'vin',
		opstat: 'status',
		status: 'status',
		openDefectCount: 'numberOfDefects',
		numberOfDefects: 'numberOfDefects',
		inspDate: 'lastInspectionStartTime',
		lastInspectionStartTime: 'lastInspectionStartTime',
		layoutName: 'layoutName',
		manufacturer: 'make',
		assetType: 'assetTypeLabel',
	};
	return mapping[field] || 'assetLabel';
};

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
	// Assets with open defects
	assetsWithDefects: null,
	assetsWithDefectsTotal: 0,
	assetsWithDefectsLoading: false,
	assetsWithDefectsError: null,

	// Filter options
	inspectionConfigTypes: [],
	assetTypes: [],
	mechanics: [],
	filterOptionsLoading: false,

	// Search results (for autocomplete)
	locationSearchResults: [],
	assetSearchResults: [],
	locationSearchLoading: false,
	assetSearchLoading: false,

	// Open defects for a specific asset
	openDefects: null,
	openDefectsTotal: 0,
	openDefectsCriticalCount: 0,
	openDefectsWarningCount: 0,
	openDefectsLoading: false,
	openDefectsError: null,

	// Defect widget summary
	defectSummary: {
		openDefects: 0,
		criticalDefects: 0,
		recentRepairs: 0,
		assetsWithDefects: 0,
	},
	defectSummaryLoading: false,

	// Repair operations
	addingRepair: false,
	addRepairError: null,
	certifyingNoRepair: false,
	certifyNoRepairError: null,
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch inspection config types for filter dropdown
 */
export const fetchInspectionConfigTypes = createAsyncThunk(
	'defectsGtc/fetchInspectionConfigTypes',
	async (_, { rejectWithValue }) => {
		try {
			const query = `
				query SearchInspectionTypes(
					$conditions: [InspectionTypeSearchInput]
					$sort: [InspectionTypeSortInput]
					$limit: Int
					$offset: Int
				) {
					searchInspectionTypes(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							legacyId
							categoryId
							name
							draft
						}
						total
					}
				}
			`;

			const data = await clientQuery(
				query,
				{
					conditions: [],
					sort: [{ field: 'name', order: 'ASC' }],
					limit: 100,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return (data.searchInspectionTypes?.items || []).map((type) => ({
				id: type.id,
				label: type.name,
				legacyId: type.legacyId,
				categoryId: type.categoryId,
			}));
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch inspection types');
		}
	}
);

/**
 * Fetch asset types for filter dropdown
 */
export const fetchAssetTypes = createAsyncThunk(
	'defectsGtc/fetchAssetTypes',
	async (_, { rejectWithValue }) => {
		try {
			const query = `
				query SearchEntityLists(
					$conditions: [EntityListSearchInput]
					$sort: [EntityListSortInput]
					$limit: Int
					$offset: Int
				) {
					searchEntityLists(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							entityId
							entityName
							parentId
							label
							value1
							value2
						}
						total
					}
				}
			`;

			const data = await clientQuery(
				query,
				{
					conditions: [
						{ fields: ['entityName'], terms: ['AssetType'], exact: true },
					],
					sort: [{ field: 'label', order: 'ASC' }],
					limit: 100,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return (data.searchEntityLists?.items || []).map((item) => ({
				id: item.id,
				name: item.label,
			}));
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch asset types');
		}
	}
);

/**
 * Search locations by name (for autocomplete)
 */
export const searchLocations = createAsyncThunk(
	'defectsGtc/searchLocations',
	async ({ query: searchQuery, limit = 20 }, { rejectWithValue }) => {
		if (!searchQuery || searchQuery.length < 2) return [];

		try {
			const query = `
				query SearchLocations(
					$conditions: [LocationSearchInput]
					$sort: [LocationSortInput]
					$limit: Int
					$offset: Int
				) {
					searchLocations(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							title
							active
						}
						total
					}
				}
			`;

			const data = await clientQuery(
				query,
				{
					conditions: [
						{ fields: ['title'], terms: [searchQuery], exact: false },
					],
					sort: [{ field: 'title', order: 'ASC' }],
					limit,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return (data.searchLocations?.items || []).map((loc) => ({
				id: loc.id,
				name: loc.title,
			}));
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to search locations');
		}
	}
);

/**
 * Search assets by fleet number (for autocomplete)
 */
export const searchAssets = createAsyncThunk(
	'defectsGtc/searchAssets',
	async ({ query: searchQuery, limit = 20 }, { rejectWithValue }) => {
		if (!searchQuery || searchQuery.trim().length === 0) return [];

		try {
			const query = `
				query SearchAssets(
					$conditions: [AssetSearchInput]
					$sort: [AssetSortInput]
					$limit: Int
					$offset: Int
				) {
					searchAssets(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							label
							unitId
							assetTypeId
							vin
							make
							model
							year
							licensePlate
						}
						total
					}
				}
			`;

			const data = await clientQuery(
				query,
				{
					conditions: [
						{ fields: ['label'], terms: [searchQuery.trim()], exact: false },
					],
					sort: [{ field: 'label', order: 'ASC' }],
					limit,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return (data.searchAssets?.items || []).map((asset) => ({
				id: asset.id,
				fleet: asset.label,
				vin: asset.vin,
				make: asset.make,
			}));
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to search assets');
		}
	}
);

/**
 * Fetch mechanics list
 */
export const fetchMechanics = createAsyncThunk(
	'defectsGtc/fetchMechanics',
	async (_, { rejectWithValue }) => {
		try {
			const query = `
				query SearchDrivers(
					$conditions: [DriverSearchInput]
					$sort: [DriverSortInput]
					$limit: Int
					$offset: Int
				) {
					searchDrivers(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							name
							empNum
							title
							status
						}
						total
					}
				}
			`;

			const data = await clientQuery(
				query,
				{
					conditions: [],
					sort: [{ field: 'title', order: 'ASC' }],
					limit: 100,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return (data.searchDrivers?.items || []).map((driver) => {
				const nameParts = (driver.name || '').split(' ');
				return {
					id: parseInt(driver.id, 10),
					firstName: nameParts[0] || '',
					lastName: nameParts.slice(1).join(' ') || '',
					name: driver.name,
					empNum: driver.empNum,
				};
			});
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch mechanics');
		}
	}
);

export const searchAssetDefectsQuery = `
	query SearchAssetDefects(
		$conditions: [AssetDefectSearchInput]
		$sort: [AssetDefectSortInput]
		$limit: Int
		$offset: Int
	) {
		searchAssetDefects(
			conditions: $conditions
			sort: $sort
			limit: $limit
			offset: $offset
		) {
			items {
				assetId
				assetLabel
				assetTypeId
				assetTypeLabel
				make
				vin
				unitId
				unitLabel
				layoutId
				layoutName
				lastInspectionStartTime
				status
				numberOfDefects
				tagIds
				tags { id label color  }
			}
			total
		}
	}
`;

export const buildAssetDefectsSearchVariables = (params) => {
	const {
		page,
		limit,
		defectStatus,
		assetTypeIds,
		assets,
		tagIds,
		configTypeIds,
		sortField,
		sortDirection,
	} = params;

	// Build conditions array
	const conditions = [];

	// Filter by defect status
	const statusEnums = mapDefectStatusToEnum(defectStatus);
	if (statusEnums.length > 0) {
		conditions.push({
			fields: ['defectStatus'],
			terms: statusEnums,
			exact: true,
		});
	}

	// Filter by asset type
	if (assetTypeIds && assetTypeIds.length > 0) {
		conditions.push({
			fields: ['assetTypeId'],
			terms: assetTypeIds,
			exact: true,
		});
	}

	// Filter by asset
	if (assets && assets.length > 0) {
		conditions.push({
			fields: ['assetId'],
			terms: assets.map((asset) => String(asset.id)),
			exact: true,
		});
	}

	// Filter by tag
	if (tagIds && tagIds.length > 0) {
		conditions.push({
			fields: ['tagIds'],
			terms: tagIds.map(String),
			exact: true,
		});
	}

	// Filter by inspection type
	if (configTypeIds && configTypeIds.length > 0) {
		conditions.push({
			fields: ['inspectionTypeId'],
			terms: configTypeIds.flat(),
			exact: true,
		});
	}

	// Build sort array
	const sort = [{
		field: mapSortField(sortField),
		order: sortDirection.toUpperCase(),
	}];

	return {
		conditions,
		sort,
		limit,
		offset: (page - 1) * limit,
	};
};

/**
 * Fetch assets with open defects report
 */
export const fetchAssetsWithOpenDefects = createAsyncThunk(
	'defectsGtc/fetchAssetsWithOpenDefects',
	async (params = {}, { rejectWithValue }) => {
		const { page = 1, limit = 20 } = params;

		try {
			const data = await clientQuery(
				searchAssetDefectsQuery,
				buildAssetDefectsSearchVariables(params),
				endpoints.GRAPHQL_GENERAL_V2
			);

			const items = data.searchAssetDefects?.items || [];
			const total = data.searchAssetDefects?.total || 0;

			// Transform to expected format
			const rows = items.map((item) => ({
				assetId: item.assetId,
				fleet: item.assetLabel,
				locationName: '',
				assetType: item.assetTypeLabel,
				manufacturer: item.make,
				vin: item.vin,
				inspDate: item.lastInspectionStartTime ? new Date(item.lastInspectionStartTime).getTime() : null,
				zoneName: '',
				gpsId: item.unitId ? `GPS${item.unitId}` : null,
				opstat: item.status,
				openDefectCount: item.numberOfDefects,
				tags: item.tags || [],
			}));

			return {
				page,
				records: total,
				total: Math.ceil(total / limit),
				rows,
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch assets with open defects');
		}
	}
);

// Map sort fields from component to GraphQL for defects
const mapDefectSortField = (field) => {
	const mapping = {
		latestInspDate: 'lastReported',
		lastReported: 'lastReported',
		ok: 'severity',
		severity: 'severity',
		repaired: 'status',
		status: 'status',
		assetLabel: 'asset_label',
		assetFleet: 'asset_label',
		// Note: zone, component, condition are not sortable in GraphQL schema
	};
	return mapping[field] || 'lastReported';
};

/**
 * Fetch open defects for a specific asset
 */
export const fetchOpenDefects = createAsyncThunk(
	'defectsGtc/fetchOpenDefects',
	async (params = {}, { rejectWithValue }) => {
		const {
			assetId,
			assetIds,
			repairedStatus = 'open',
			zoneNames,
			componentNames,
			conditionNames,
			page = 1,
			limit = 20,
			sortField = 'lastReported',
			sortDirection = 'desc',
		} = params;

		try {
			const query = `
				query SearchDefects(
					$conditions: [DefectSearchInput]
					$sort: [DefectSortInput]
					$limit: Int
					$offset: Int
				) {
					searchDefects(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							inspectionId
							inspection {
								id
								startTime
							}
							assetId
							asset {
								id
								label
								licensePlate
								vin
							}
							driverId
							driver {
								id
								name
							}
							legacyId
							draft
							firstReported
							lastReported
							severity
							status
							layoutId
							typeId
							zone
							component
							condition
							repairs {
								createdOn
								status
								data
							}
							data
							createdOn
							updatedOn
						}
						total
					}
				}
			`;

			// Build conditions
			const conditions = [];

			if (assetId) {
				conditions.push({
					fields: ['assetId'],
					terms: [String(assetId)],
					exact: true,
				});
			} else if (assetIds && assetIds.length > 0) {
				conditions.push({
					fields: ['assetId'],
					terms: assetIds.map((id) => String(id)),
					exact: true,
				});
			}

			// Filter by status
			let statusFilter = ['OPEN', 'PENDING'];
			if (repairedStatus === 'open') {
				statusFilter = ['OPEN', 'PENDING'];
			} else if (repairedStatus === 'repaired') {
				statusFilter = ['REPAIRED'];
			} else if (repairedStatus === 'ignored') {
				statusFilter = ['IGNORED'];
			}
			if (repairedStatus !== 'all') {
				conditions.push({
					fields: ['status'],
					terms: statusFilter,
					exact: true,
				});
			}

			if (zoneNames && zoneNames.length > 0) {
				conditions.push({
					fields: ['zone'],
					terms: zoneNames,
					exact: true,
				});
			}

			if (componentNames && componentNames.length > 0) {
				conditions.push({
					fields: ['component'],
					terms: componentNames,
					exact: true,
				});
			}

			if (conditionNames && conditionNames.length > 0) {
				conditions.push({
					fields: ['condition'],
					terms: conditionNames,
					exact: true,
				});
			}

			// Build sort array
			const sort = [{
				field: mapDefectSortField(sortField),
				order: sortDirection.toUpperCase(),
			}];

			const data = await clientQuery(
				query,
				{
					conditions,
					sort,
					limit,
					offset: (page - 1) * limit,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			const total = data.searchDefects?.total || 0;
			const defects = (data.searchDefects?.items || []).map((defect) => {
				// Parse the data field to extract photos
				let defectData = {};
				if (defect.data) {
					try {
						defectData = typeof defect.data === 'string' ? JSON.parse(defect.data) : defect.data;
					} catch (e) {
						defectData = {};
					}
				}
				const defectMedia = defectData.defectMedia || [];
				const photos = defectMedia
					.filter((media) => media.thumbnail)
					.map((media) => ({
						mediaId: media.mediaId,
						mediaType: media.mediaType,
						thumbnail: media.thumbnail,
						url: `data:image/${media.mediaType || 'jpeg'};base64,${media.thumbnail}`,
						...(media.fileKey ? { fileKey: media.fileKey } : {}),
					}));

				return {
					defectId: defect.id,
					assetId: defect.assetId,
					assetFleet: defect.asset?.label || '',
					inspId: defect.inspectionId,
					latestInspDate: defect.lastReported,
					componentId: null,
					componentName: defect.component,
					conditionId: null,
					conditionName: defect.condition,
					zoneId: null,
					zoneName: defect.zone,
					severity: defect.severity,
					ok: defect.severity < 127,
					defectCount: 1,
					pending: defect.status === 'PENDING',
					repaired: defect.status === 'REPAIRED',
					ignored: defect.status === 'IGNORED',
					repairs: defect.repairs || [],
					photos,
				};
			});

			return {
				defects,
				total,
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch defects');
		}
	}
);

/**
 * Fetch open defects counts (critical and warning) based on filters
 */
export const fetchOpenDefectsCounts = createAsyncThunk(
	'defectsGtc/fetchOpenDefectsCounts',
	async (params = {}, { rejectWithValue }) => {
		const {
			assetId,
			assetIds,
			repairedStatus = 'open',
			zoneNames,
			componentNames,
			conditionNames,
		} = params;

		try {
			const query = `
				query CountDefects($conditions: [DefectSearchInput]) {
					countDefects(conditions: $conditions) {
						totalDefects
						criticalDefects
						minorDefects
					}
				}
			`;

			// Build base conditions (same as fetchOpenDefects)
			const conditions = [];

			if (assetId) {
				conditions.push({
					fields: ['assetId'],
					terms: [String(assetId)],
					exact: true,
				});
			} else if (assetIds && assetIds.length > 0) {
				conditions.push({
					fields: ['assetId'],
					terms: assetIds.map((id) => String(id)),
					exact: true,
				});
			}

			// Filter by status
			let statusFilter = ['OPEN', 'PENDING'];
			if (repairedStatus === 'open') {
				statusFilter = ['OPEN', 'PENDING'];
			} else if (repairedStatus === 'repaired') {
				statusFilter = ['REPAIRED'];
			} else if (repairedStatus === 'ignored') {
				statusFilter = ['IGNORED'];
			}
			if (repairedStatus !== 'all') {
				conditions.push({
					fields: ['status'],
					terms: statusFilter,
					exact: true,
				});
			}

			if (zoneNames && zoneNames.length > 0) {
				conditions.push({
					fields: ['zone'],
					terms: zoneNames,
					exact: true,
				});
			}

			if (componentNames && componentNames.length > 0) {
				conditions.push({
					fields: ['component'],
					terms: componentNames,
					exact: true,
				});
			}

			if (conditionNames && conditionNames.length > 0) {
				conditions.push({
					fields: ['condition'],
					terms: conditionNames,
					exact: true,
				});
			}

			const data = await clientQuery(
				query,
				{ conditions },
				endpoints.GRAPHQL_GENERAL_V2
			);

			return {
				criticalCount: data.countDefects?.criticalDefects || 0,
				warningCount: data.countDefects?.minorDefects || 0,
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch defect counts');
		}
	}
);

/**
 * Fetch defect widget summary
 */
export const fetchDefectWidgetSummary = createAsyncThunk(
	'defectsGtc/fetchDefectWidgetSummary',
	async (_, { rejectWithValue }) => {
		try {
			const defectsQuery = `
				query SearchDefects(
					$conditions: [DefectSearchInput]
					$sort: [DefectSortInput]
					$limit: Int
					$offset: Int
				) {
					searchDefects(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							severity
						}
						total
					}
				}
			`;

			const assetDefectsQuery = `
				query SearchAssetDefects(
					$conditions: [AssetDefectSearchInput]
					$sort: [AssetDefectSortInput]
					$limit: Int
					$offset: Int
				) {
					searchAssetDefects(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							assetId
						}
						total
					}
				}
			`;

			// Fetch open defects count
			const openResult = await clientQuery(
				defectsQuery,
				{
					conditions: [
						{ fields: ['status'], terms: ['OPEN', 'PENDING'], exact: true },
					],
					limit: 1,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			// Fetch critical defects
			const criticalResult = await clientQuery(
				defectsQuery,
				{
					conditions: [
						{ fields: ['status'], terms: ['OPEN', 'PENDING'], exact: true },
					],
					limit: 500,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			// Fetch assets with defects count
			const assetResult = await clientQuery(
				assetDefectsQuery,
				{
					conditions: [
						{ fields: ['defectStatus'], terms: ['OPEN', 'PENDING'], exact: true },
					],
					limit: 1,
					offset: 0,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			const openTotal = openResult.searchDefects?.total || 0;
			const criticalDefects = (criticalResult.searchDefects?.items || [])
				.filter((d) => d.severity >= 3);
			const assetsWithDefects = assetResult.searchAssetDefects?.total || 0;

			return {
				openDefects: openTotal,
				criticalDefects: criticalDefects.length,
				recentRepairs: 0,
				assetsWithDefects,
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch defect summary');
		}
	}
);

/**
 * Add a repair to a defect
 */
export const addRepair = createAsyncThunk(
	'defectsGtc/addRepair',
	async (repairData, { rejectWithValue }) => {
		const {
			defectId,
			mechanicId,
			soNumber,
			comment,
			assetId,
			inspectionId,
			mechanicFirstName,
			status,
			resolution,
		} = repairData;

		try {
			const mutation = `
				mutation CreateRepair(
					$id: ID!
					$defectId: ID!
					$assetId: Int!
					$mechanicId: Int!
					$status: DefectStatuses!
					$comment: String
					$draft: Boolean
					$expectedDate: AWSDateTime
					$inspectionId: String
					$mechanicFirstName: String
					$mechanicLastName: String
					$resolution: String
					$resolutionType: RepairResolutionTypes
					$workOrder: String
				) {
					createRepair(
						id: $id
						defectId: $defectId
						assetId: $assetId
						mechanicId: $mechanicId
						status: $status
						comment: $comment
						draft: $draft
						expectedDate: $expectedDate
						inspectionId: $inspectionId
						mechanicFirstName: $mechanicFirstName
						mechanicLastName: $mechanicLastName
						resolution: $resolution
						resolutionType: $resolutionType
						workOrder: $workOrder
					) {
						id
						inspectionId
						defectId
						status
						createdOn
						createdBy
					}
				}
			`;

			const repairId = uuidv4();

			const data = await clientMutation(
				mutation,
				{
					id: repairId,
					defectId: String(defectId),
					assetId: Number(assetId),
					mechanicId: Number(mechanicId),
					inspectionId,
					mechanicFirstName,
					status,
					comment,
					...(soNumber ? { workOrder: soNumber } : {}),
					...(resolution ? { resolutionType: resolution, resolution: resolution.toLocaleLowerCase() } : {}),
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return {
				repairId: data.createRepair?.[0]?.id || repairId,
				message: 'Repair added successfully',
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to add repair');
		}
	}
);

/**
 * Certify that a defect requires no repair
 */
export const certifyNoRepair = createAsyncThunk(
	'defectsGtc/certifyNoRepair',
	async (certificationData, { rejectWithValue }) => {
		const { defectId, additionalNotes } = certificationData;

		try {
			const mutation = `
				mutation UpdateDefect(
					$id: ID!
					$status: String
					$severity: Int
					$data: AWSJSON
				) {
					updateDefect(id: $id, status: $status, severity: $severity, data: $data) {
						id
						status
						severity
						updatedOn
						updatedBy
					}
				}
			`;

			const data = await clientMutation(
				mutation,
				{
					id: String(defectId),
					status: 'IGNORED',
					data: additionalNotes ? JSON.stringify({ notes: additionalNotes }) : null,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return {
				certificationId: data.updateDefect?.id,
				message: 'Defect certified as requiring no repair',
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to certify defect');
		}
	}
);

// ============================================================================
// Slice
// ============================================================================

const defectsGtcSlice = createSlice({
	name: 'defectsGtc',
	initialState,
	reducers: {
		reset: () => initialState,
		clearLocationSearchResults: (state) => {
			state.locationSearchResults = [];
		},
		clearAssetSearchResults: (state) => {
			state.assetSearchResults = [];
		},
	},
	extraReducers: (builder) => {
		// Fetch Inspection Config Types
		builder
			.addCase(fetchInspectionConfigTypes.pending, (state) => {
				state.filterOptionsLoading = true;
			})
			.addCase(fetchInspectionConfigTypes.fulfilled, (state, action) => {
				state.filterOptionsLoading = false;
				state.inspectionConfigTypes = action.payload;
			})
			.addCase(fetchInspectionConfigTypes.rejected, (state) => {
				state.filterOptionsLoading = false;
			});

		// Fetch Asset Types
		builder
			.addCase(fetchAssetTypes.pending, (state) => {
				state.filterOptionsLoading = true;
			})
			.addCase(fetchAssetTypes.fulfilled, (state, action) => {
				state.filterOptionsLoading = false;
				state.assetTypes = action.payload;
			})
			.addCase(fetchAssetTypes.rejected, (state) => {
				state.filterOptionsLoading = false;
			});

		// Search Locations
		builder
			.addCase(searchLocations.pending, (state) => {
				state.locationSearchLoading = true;
			})
			.addCase(searchLocations.fulfilled, (state, action) => {
				state.locationSearchLoading = false;
				state.locationSearchResults = action.payload;
			})
			.addCase(searchLocations.rejected, (state) => {
				state.locationSearchLoading = false;
			});

		// Search Assets
		builder
			.addCase(searchAssets.pending, (state) => {
				state.assetSearchLoading = true;
			})
			.addCase(searchAssets.fulfilled, (state, action) => {
				state.assetSearchLoading = false;
				state.assetSearchResults = action.payload;
			})
			.addCase(searchAssets.rejected, (state) => {
				state.assetSearchLoading = false;
			});

		// Fetch Mechanics
		builder
			.addCase(fetchMechanics.fulfilled, (state, action) => {
				state.mechanics = action.payload;
			});

		// Fetch Assets With Open Defects
		builder
			.addCase(fetchAssetsWithOpenDefects.pending, (state) => {
				state.assetsWithDefectsLoading = true;
				state.assetsWithDefectsError = null;
			})
			.addCase(fetchAssetsWithOpenDefects.fulfilled, (state, action) => {
				state.assetsWithDefectsLoading = false;
				state.assetsWithDefects = action.payload.rows;
				state.assetsWithDefectsTotal = action.payload.records;
			})
			.addCase(fetchAssetsWithOpenDefects.rejected, (state, action) => {
				state.assetsWithDefectsLoading = false;
				state.assetsWithDefectsError = action.payload;
			});

		// Fetch Open Defects
		builder
			.addCase(fetchOpenDefects.pending, (state) => {
				state.openDefectsLoading = true;
				state.openDefectsError = null;
			})
			.addCase(fetchOpenDefects.fulfilled, (state, action) => {
				state.openDefectsLoading = false;
				state.openDefects = action.payload.defects;
				state.openDefectsTotal = action.payload.total;
			})
			.addCase(fetchOpenDefects.rejected, (state, action) => {
				state.openDefectsLoading = false;
				state.openDefectsError = action.payload;
			});

		// Fetch Open Defects Counts
		builder
			.addCase(fetchOpenDefectsCounts.fulfilled, (state, action) => {
				state.openDefectsCriticalCount = action.payload.criticalCount;
				state.openDefectsWarningCount = action.payload.warningCount;
			});

		// Fetch Defect Widget Summary
		builder
			.addCase(fetchDefectWidgetSummary.pending, (state) => {
				state.defectSummaryLoading = true;
			})
			.addCase(fetchDefectWidgetSummary.fulfilled, (state, action) => {
				state.defectSummaryLoading = false;
				state.defectSummary = action.payload;
			})
			.addCase(fetchDefectWidgetSummary.rejected, (state) => {
				state.defectSummaryLoading = false;
			});

		// Add Repair
		builder
			.addCase(addRepair.pending, (state) => {
				state.addingRepair = true;
				state.addRepairError = null;
			})
			.addCase(addRepair.fulfilled, (state, action) => {
				state.addingRepair = false;
				// Optimistically update the defect in local state
				if (state.openDefects && action.meta?.arg) {
					const { defectId, status } = action.meta.arg;
					const defectIndex = state.openDefects.findIndex(d => d.defectId === defectId);
					if (defectIndex !== -1) {
						state.openDefects[defectIndex].repaired = status === 'REPAIRED';
						state.openDefects[defectIndex].ignored = status === 'IGNORED';
						state.openDefects[defectIndex].pending = status === 'PENDING';
					}
				}
			})
			.addCase(addRepair.rejected, (state, action) => {
				state.addingRepair = false;
				state.addRepairError = action.payload;
			});

		// Certify No Repair
		builder
			.addCase(certifyNoRepair.pending, (state) => {
				state.certifyingNoRepair = true;
				state.certifyNoRepairError = null;
			})
			.addCase(certifyNoRepair.fulfilled, (state) => {
				state.certifyingNoRepair = false;
			})
			.addCase(certifyNoRepair.rejected, (state, action) => {
				state.certifyingNoRepair = false;
				state.certifyNoRepairError = action.payload;
			});

		// Global reset
		builder.addCase(resetALL, () => initialState);
	},
});

export const { reset, clearLocationSearchResults, clearAssetSearchResults } = defectsGtcSlice.actions;

export default defectsGtcSlice.reducer;
